import { inject, injectable, interfaces } from 'inversify';
import { IConfiguration } from '../../../configuration/config.types';
import { IMainApiService } from '../../services/api.service';
import { IApiService } from '../../services/api.service.base';
import { NotificationLevel, ToastNotification } from '../notifications/models/notification.model';
import { INotificationsService } from '../notifications/services/notifications.service';
import { IAnnotationApiService } from './annotationApi.service';
import { IAnnotationsStore } from './annotations.store';
import { IAnnotationUiStore } from './annotationUi.store';
import { IImagesQueueService, ImagesQueueServiceBase } from './imagesQueueServiceBase';

export interface IAnnotationmagesQueueService extends IImagesQueueService {
  setup(projectId: string): void;
}

export namespace IAnnotationmagesQueueService {
  export const $: interfaces.ServiceIdentifier<IAnnotationmagesQueueService> = Symbol('ANNOTATION_IMAGES_QUEUE_SERVICE');
}

@injectable()
export class AnnotationImagesQueueService extends ImagesQueueServiceBase implements IAnnotationmagesQueueService {
  annotationApiService!: IAnnotationApiService;
  uiData: IAnnotationUiStore;
  notificationService: INotificationsService;

  constructor(
    @inject(IMainApiService.$) apiService: IApiService,
    @inject(IAnnotationApiService.$) annotationApiService: IAnnotationApiService,
    @inject(IAnnotationUiStore.$) uiData: IAnnotationUiStore,
    @inject(INotificationsService.$) notificationService: INotificationsService,
    @inject(IConfiguration.$) configuration: IConfiguration,
    @inject(IAnnotationsStore.$) private readonly annotationsStore: IAnnotationsStore
  ) {
    super(configuration, apiService, (value: boolean) => {
      this.uiData.isQueueRequestInProgress = value;
    });

    this.annotationApiService = annotationApiService;
    this.uiData = uiData;
    this.notificationService = notificationService;
  }

  public setup(projectId: string): void {
    super.setupBase(
      async () => this.annotationApiService.getImageForAnnotationAsync(projectId, this.removeLocks),
      2,
      async (imageId: string) => this.annotationApiService.reLockImageForAnnotationAsync(imageId, projectId),
      this.handleLockRefreshFail
    );
  }

  private handleLockRefreshFail = async (imageId: string): Promise<void> => {
    if (this.annotationsStore.image?.id === imageId) {
      await this.notificationService.push(new ToastNotification(NotificationLevel.ERROR, 'image_is_locked_by_another_user'));
      setTimeout(() => {
        window.location.reload();
      }, 4000);
    }
  };
}
